import clsx from 'clsx'
import palette from 'constants/palette'
import usePrefersReducedMotion from 'hooks/usePrefersReducedMotion'
import { buildTranslate } from 'locales'
import React from 'react'
import { useCyclingIndex } from './useCyclingIndex'
import './whyItMatters.scss'

const t = buildTranslate('live_updates.overview.why.big_leagues')

const CYCLE_MS = 2600
// The DoorDash card's rail is 63% travelled between its four stops.
const DELIVERY_PROGRESS = '63%'
const UBER_PROGRESS = '78%'

// One-off glyphs for a decorative mock, inline rather than added to the shared
// icon library: they exist only to stand in for the brands this card names and
// have no other caller. Shapes are the Sketch prototype's own
// (live-updates-onboarding v31) — generic marks in each brand's color, not
// reproductions of anyone's logo.
const ChevronGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={palette.white}
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
)

const GlobeGlyph = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={palette.white} strokeWidth="1.5">
    <circle cx="12" cy="12" r="8.5" />
    <ellipse cx="12" cy="12" rx="3.4" ry="8.5" />
    <path d="M3.5 12h17" />
  </svg>
)

const TriangleGlyph = () => (
  <svg viewBox="0 0 24 24">
    {/* The named brand's own red — a deliberate deviation from the
        theme, like the logoColor values below. */}
    <path d="M12 3 21 19H3z" fill="#C01933" />
  </svg>
)

const LeafGlyph = () => (
  <svg viewBox="0 0 24 24" fill={palette.white}>
    <path d="M12 3c-3.4 3-3.4 8 0 11 3.4-3 3.4-8 0-11z" />
    <path d="M11.3 13.6h1.4V21h-1.4z" />
  </svg>
)

const DotNode = () => (
  <svg viewBox="0 0 24 24" fill={palette.white}>
    <circle cx="12" cy="12" r="6" />
  </svg>
)

const BagNode = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={palette.white} strokeWidth="2">
    <path d="M4 9l2-5h12l2 5" />
    <rect x="4" y="9" width="16" height="9" rx="1" />
  </svg>
)

const CarNode = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={palette.white} strokeWidth="2">
    <rect x="3" y="11" width="18" height="5" rx="2" />
    <circle cx="7.5" cy="16.5" r="1.6" fill={palette.white} />
    <circle cx="16.5" cy="16.5" r="1.6" fill={palette.white} />
  </svg>
)

const HouseNode = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke={palette.white} strokeWidth="2">
    <path d="M4 11l8-7 8 7" />
    <path d="M6 10v9h12v-9" />
  </svg>
)

const DELIVERY_NODES = [
  { key: 'ordered', glyph: <DotNode />, lit: true },
  { key: 'packed', glyph: <BagNode />, lit: true },
  { key: 'driving', glyph: <CarNode />, lit: true },
  { key: 'arrived', glyph: <HouseNode />, lit: false },
]

const CardHead = ({
  logo,
  logoColor,
  name,
  meta,
  metaAsPill,
}: {
  logo: React.ReactNode
  logoColor?: string
  name: string
  meta: string
  metaAsPill?: boolean
}) => (
  <div className="lu-bl-head">
    <span
      className="lu-bl-logo"
      style={logoColor ? { background: logoColor } : undefined}
    >
      {logo}
    </span>
    <b className="lu-bl-name">{name}</b>
    {metaAsPill ? (
      <span className="lu-bl-pill lu-bl-pill--trailing">{meta}</span>
    ) : (
      <span className="lu-bl-meta">{meta}</span>
    )}
  </div>
)

const DeliveryCard = () => (
  <>
    <CardHead
      logo={<ChevronGlyph />}
      logoColor="#EB1700"
      name={t('delivery.name')}
      meta={t('delivery.meta')}
    />
    <div className="lu-bl-eta">
      <b>{t('delivery.countdown')}</b> {t('delivery.countdown_suffix')}
    </div>
    <div className="lu-bl-track-row">
      <span className="lu-bl-track" />
      <span className="lu-bl-progress" style={{ width: DELIVERY_PROGRESS }} />
      {DELIVERY_NODES.map(node => (
        <span
          key={node.key}
          className={clsx('lu-bl-node', { 'is-lit': node.lit })}
        >
          {node.glyph}
        </span>
      ))}
    </div>
  </>
)

const BoardingCard = () => (
  <>
    <CardHead
      logo={<GlobeGlyph />}
      logoColor="#00274D"
      name={t('boarding.name')}
      meta={t('boarding.meta')}
    />
    <div className="lu-bl-row">
      <span>{t('boarding.seat')}</span>
      <span>{t('boarding.route')}</span>
    </div>
    <div className="lu-bl-headline">{t('boarding.headline')}</div>
    <div className="lu-bl-foot">
      <span>{t('boarding.gate')}</span>
      <span className="lu-bl-pill">{t('boarding.status')}</span>
    </div>
  </>
)

const FlightCard = () => (
  <>
    <CardHead
      logo={<TriangleGlyph />}
      name={t('flight.name')}
      meta={t('flight.status')}
      metaAsPill
    />
    <div className="lu-bl-headline">{t('flight.headline')}</div>
    <div className="lu-bl-sub">{t('flight.terminal')}</div>
    <div className="lu-bl-row lu-bl-row--spaced">
      <span>{t('flight.depart')}</span>
      <span>{t('flight.arrive')}</span>
    </div>
  </>
)

const CourierCard = () => (
  <>
    <CardHead
      logo={<LeafGlyph />}
      logoColor="#06C167"
      name={t('courier.name')}
      meta={t('courier.meta')}
    />
    <div className="lu-bl-headline">{t('courier.headline')}</div>
    <div className="lu-bl-sub">{t('courier.driver')}</div>
    <div className="lu-bl-bar">
      <span style={{ width: UBER_PROGRESS }} />
    </div>
    <div className="lu-bl-row is-actions">
      <span>{t('courier.call')}</span>
      <span>{t('courier.message')}</span>
      <span>{t('courier.tip')}</span>
    </div>
  </>
)

const CARDS = [
  { key: 'delivery', light: false, Card: DeliveryCard },
  { key: 'boarding', light: false, Card: BoardingCard },
  { key: 'flight', light: false, Card: FlightCard },
  { key: 'courier', light: true, Card: CourierCard },
]

// Decorative: a phone cycling the live-activity cards guests already get from
// big consumer brands, so a merchant can see the company their own card would
// be keeping (Sketch prototype live-updates-onboarding v31, `.bigleagues`).
// Frozen on the first card when the visitor prefers reduced motion.
const BigLeaguesPhone = () => {
  const prefersReducedMotion = usePrefersReducedMotion()
  const active = useCyclingIndex(CARDS.length, !prefersReducedMotion, CYCLE_MS)

  return (
    <div aria-hidden="true" data-testid="lu-overview-big-leagues">
      <div className="lu-bl-phone">
        <div className="lu-bl-lock">
          <div className="lu-bl-notch" />
          <div className="lu-bl-time">{t('clock')}</div>
          <div className="lu-bl-stack">
            {CARDS.map(({ key, light, Card }, index) => (
              <div
                key={key}
                data-card={key}
                className={clsx('lu-bl-card', {
                  'is-active': index === active,
                  'is-light': light,
                })}
              >
                <Card />
              </div>
            ))}
          </div>
          <div className="lu-bl-dots">
            {CARDS.map(({ key }, index) => (
              <span
                key={key}
                className={clsx({ 'is-active': index === active })}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BigLeaguesPhone
